import { Form } from 'antd'
import { TAdditionalValueForm, TAdditionalValueFormErrors } from './types'
import { useCallback, useEffect, useState } from 'react'
import { TOption } from '../../../components/Select/types'
import {
  addAdditionalValues,
  editAdditionalValues,
  getAdditionalValue,
  getCCOptionsRequest,
  getHospitalOptionsRequest,
  getHSOptionsRequest,
  getSourcingPersonOptionsRequest,
  getSubtypeOptionsRequest,
  getTypeOptionsRequest
} from './api'
import { formatOptionIdValue } from '../../../helper/optionsFormatters'
import { useAdditionalValuesPopup } from '../Providers/AdditionalValuesPopupProvider'
import { setLoading } from '../../../redux/store/common/slice'
import { useDispatch } from 'react-redux'
import { useAdditionalValuesContext } from '../Providers/AdditionalValuesContextProvider'
import { AxiosResponse } from 'axios'

type Props = {
  predefinedHSValue?: string
  onSaveCallback: () => void
}

export const useAddEditAdditionalValuesForm = ({
  predefinedHSValue,
  onSaveCallback
}: Props) => {
  const dispatch = useDispatch()

  const [form] = Form.useForm<TAdditionalValueForm>()
  const [errors, setErrors] = useState<TAdditionalValueFormErrors>({})

  const {
    addEditAdditionalValuesPopup: { actions, state }
  } = useAdditionalValuesPopup()

  const {
    state: { selectedAdditionalValue },
    actions: { setSelectedAdditionalValue }
  } = useAdditionalValuesContext()

  const selectedType = Form.useWatch('type', form)
  const selectedHS = Form.useWatch('health_system', form)
  const selectedSourcingPerson = Form.useWatch('sourcing_person', form)

  const [typeOptions, setTypeOptions] = useState<TOption[]>([])
  const [subtypeOptions, setSubtypeOptions] = useState<TOption[]>([])

  const [hsOptions, setHSOptions] = useState<TOption[]>([])
  const [hospitalOptions, setHospitalOptions] = useState<TOption[]>([])

  const [sourcingOptions, setSourcingOptions] = useState<TOption[]>([])
  const [categoryOptions, setCategoryOptions] = useState<TOption[]>([])

  const getTypeOptions = useCallback(async () => {
    const result = await getTypeOptionsRequest()
    setTypeOptions(result.data.results.map(formatOptionIdValue))
  }, [])

  const getSubtypeOptions = useCallback(async () => {
    const result = await getSubtypeOptionsRequest({ type: selectedType })
    setSubtypeOptions(result.data.results.map(formatOptionIdValue))
  }, [selectedType])

  const getHSOptions = useCallback(async () => {
    const result = await getHSOptionsRequest()
    setHSOptions(result.data.results.map(formatOptionIdValue))
    if (predefinedHSValue) {
      form.setFieldsValue({
        health_system: predefinedHSValue
      })
    }
  }, [form, predefinedHSValue])

  const getHospitalOptions = useCallback(async () => {
    const result = await getHospitalOptionsRequest({
      health_system_id: selectedHS
    })
    setHospitalOptions(result.data.results.map(formatOptionIdValue))
  }, [selectedHS])

  const getSourcingPersonOptions = useCallback(async () => {
    const result = await getSourcingPersonOptionsRequest({
      health_system_id: selectedHS
    })
    setSourcingOptions(
      result.data.results.map(({ uuid, first_name, last_name }) => ({
        value: uuid,
        label: `${first_name} ${last_name}`
      }))
    )
  }, [selectedHS])

  const getCCOptions = useCallback(async () => {
    const result = await getCCOptionsRequest({
      sourcing_person_id: selectedSourcingPerson
    })
    setCategoryOptions(result.data.results.map(formatOptionIdValue))
  }, [selectedSourcingPerson])

  useEffect(() => {
    if (state.visible) {
      getTypeOptions()
      getHSOptions()
    }
  }, [state.visible, getHSOptions, getTypeOptions])

  useEffect(() => {
    if (selectedType) getSubtypeOptions()
  }, [selectedType, getSubtypeOptions])

  useEffect(() => {
    if (selectedHS) {
      getHospitalOptions()
      getSourcingPersonOptions()
    }
  }, [selectedHS, getHospitalOptions, getSourcingPersonOptions])

  useEffect(() => {
    if (selectedSourcingPerson) getCCOptions()
  }, [selectedSourcingPerson, getCCOptions])

  const onSave = useCallback(
    async (values: TAdditionalValueForm) => {
      dispatch(setLoading(true))
      try {
        const additionalValuesToAdd = {
          ...values,
          subtype: values.subtype ?? null,
          description: !!values.description ? values.description : null,
          amount_usd: Number(values.amount_usd).toFixed(2)
        }
        if (selectedAdditionalValue) {
          // edit
          await editAdditionalValues(
            selectedAdditionalValue.uuid,
            additionalValuesToAdd
          )
        } else {
          await addAdditionalValues(additionalValuesToAdd)
        }
        actions.close()
        onSaveCallback()
      } catch (error) {
        const typedResponse = error as AxiosResponse
        setErrors(typedResponse.data)
      } finally {
        dispatch(setLoading(false))
      }
    },
    [actions, dispatch, selectedAdditionalValue, onSaveCallback]
  )

  const predefineFormData = useCallback(async () => {
    if (selectedAdditionalValue) {
      // set form values
      const { data } = await getAdditionalValue(selectedAdditionalValue.uuid)
      form.setFieldsValue({
        ...data,
        sourcing_person: data.sourcing_person?.uuid,
        health_system: data.health_system.uuid,
        hospital: data.hospital?.uuid,
        category: data.category?.uuid
      } as TAdditionalValueForm)
    }
  }, [selectedAdditionalValue, form])

  const resetFormData = useCallback(() => {
    setSelectedAdditionalValue()
    form.resetFields()
    setErrors({})
  }, [form, setSelectedAdditionalValue])

  useEffect(() => {
    if (selectedAdditionalValue && state.visible) predefineFormData()
  }, [selectedAdditionalValue, state.visible, predefineFormData])

  useEffect(() => {
    if (!state.visible) resetFormData()
  }, [state.visible, resetFormData])

  return {
    form,
    errors,
    setErrors,
    typeOptions,
    subtypeOptions,
    hsOptions,
    hospitalOptions,
    sourcingOptions,
    categoryOptions,
    selectedHS,
    selectedType,
    selectedSourcingPerson,
    onSave
  }
}
